<template>
  <div class="operator-group-edit px-8">
    <FlyoutMenuHead :selectedItem="selectedItem" type="edit" class="mb-3"/>
    <v-card flat class="edit-card scrollable-container">
      <!-- Main Info Section -->
      <v-card-text class="py-4 px-0">
        <div class="section-header mb-4">
          <h3 class="text-subtitle-1 font-weight-medium">Main Info</h3>
        </div>
        <!--row with first content name and active-->
        <v-row class="mb-0 pb-0">
          <v-col class="mb-0 pb-0" cols="12" md="6">
            <v-text-field
                  v-model="operatorGroup.name"
                  label="Name"
                  outlined
                  dense
                  counter="64"
                  placeholder="Enter group name"/>
          </v-col>
          <v-col class="mb-0 pb-0" cols="12" md="6">
            <v-col class="pt-0">
              <div class="col-border">
                <v-checkbox
                  v-model="operatorGroup.active"
                  class="pl-3"
                  label="Active"
                  dense
                />
              </div>
              </v-col>
          </v-col>
        </v-row>
        <!--row with second content description and smart group -->
        <v-row>
          <v-col cols="12" md="6">
              <v-textarea
                  v-model="operatorGroup.description"
                  label="Description"
                  outlined
                  dense
                  counter="200"
                  placeholder="Enter group description"
                  rows="3"
                />
          </v-col>
          <v-col cols="12" md="6" class="px-6">
              <div class="row-border">
                <v-checkbox
                  v-model="operatorGroup.smartGroup"
                  label="Automatic Operator group"
                  class="pb-0 mb-0 mt-0 pt-0"
                  dense
                  hide-details/>
                <div class="text-caption text--secondary mt-0 pl-8 mt-0 pt-0">
                  Groups marked as "Automatic Operator group" are assigned to newly created operators by default.
                </div>
              </div>
          </v-col>
        </v-row>

      </v-card-text>
      <!-- Operators List Section -->
      <v-card-text class="pl-0 pr-4 py-0">
        <div class="section-header mb-4 d-flex justify-space-between align-center">
          <h3 class="text-subtitle-1 font-weight-medium">Operators</h3>
        </div>
        <v-data-table
          :headers="operatorHeaders"
          :items="operatorItems"
          :items-per-page="-1"
          class="elevation-0 table-style"
          outlined
          elevation="0"
          hide-default-footer
        >
          <template slot="item.username" slot-scope="{ item }">
            <div class="d-flex align-center">
              <v-icon
                size="8"
                :color="item.online ? 'green' : 'red'"
                class="mr-2"
              >{{ items.mdiCircle }}</v-icon>
              <span class="text-body-2">{{ item.username }}</span>
            </div>
          </template>
          <template slot="item.gender" slot-scope="{ item }">
            <span class="text-body-2">{{ item.gender || '-' }}</span>
          </template>
          <template slot="item.enabled" slot-scope="{ item }">
            <v-chip x-small :color="item.enabled ? 'success' : 'error'" text-color="white">
              {{ item.enabled ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>
          <template slot="item.tags" slot-scope="{ item }">
            <div v-if="item.tags && item.tags.length > 0">
              <v-chip v-for="tag in item.tags.slice(0, 2)" :key="tag" x-small class="mr-1">{{ tag }}</v-chip>
              <span v-if="item.tags.length > 2" class="text-caption grey--text">+{{ item.tags.length - 2 }}</span>
            </div>
            <span v-else class="text-body-2">-</span>
          </template>
          <template slot="item.actions" slot-scope="{ item }">
            <v-btn icon x-small color="error" @click="removeOperatorFromGroup(item)" :disabled="updatingOperators">
              <v-icon size="16">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        <div class="positioned-button-top-right">
          <v-btn
            color="primary"
            @click="openOperatorDialog"
            :loading="updatingOperators"
            class="text-capitalize mt-4"
            elevation="0"
            small
          >Add Operators</v-btn>
        </div>

        <!-- Operator Selection Dialog -->
        <v-dialog v-model="operatorDialog" max-width="600px" persistent>
          <v-card class="pa-6">
            <v-card-title>
              <span class="text-h6">Select Operators</span>
              <v-spacer />
              <v-btn icon @click="closeOperatorDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-text-field
                v-model="searchOperators"
                label="Search operators..."
                placeholder="Search by name, email, or username"
                outlined
                dense
                prepend-inner-icon="mdi-magnify"
                clearable
                class="mb-3"
              />
              <div class="operators-selection-list">
                <v-list dense max-height="400" class="overflow-y-auto">
                  <v-list-item
                    v-for="op in filteredAvailableOperators"
                    :key="op.uuid"
                    class="operator-select-item"
                    @click="toggleOperator(op)"
                  >
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="isOperatorSelected(op)"
                        @change="toggleOperator(op)"
                        color="primary"
                        dense
                        class="ma-0 pa-0"
                        @click.stop
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        {{ op.first_name }} {{ op.last_name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">{{ op.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="filteredAvailableOperators.length === 0">
                    <v-list-item-content>
                      <v-list-item-title>No operators found</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-chip v-if="tempSelectedOperators.length > 0" small color="primary" class="ml-2">
                {{ tempSelectedOperators.length }} selected
              </v-chip>
              <v-spacer />
              <v-btn class="text-capitalize" text @click="closeOperatorDialog">
                Cancel
              </v-btn>
              <v-btn 
                color="primary" 
                @click="confirmOperatorSelection"
                :loading="updatingOperators"
                class="text-capitalize"
              >
                Save Selection
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
      <!-- Permissions Section -->
      <v-card-text class="pl-0 pr-3 py-4">
        <GroupPermissions
        class="pr-2"
          :form="operatorGroupForm"
          :groupUuid="groupUuid"
          @update:form="onPermissionsFormUpdate"
        />
      </v-card-text>
    </v-card>
    <FormFooter :currentStep="1" :totalSteps="1" @save="updateOperator" @cancel="closeFlyout" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FlyoutMenuHead from '../FlyoutMenuHead.vue';
import FormFooter from './FormFooter.vue';
import GroupPermissions from './GroupPermissions.vue';
import { mdiCircle } from "@mdi/js";

export default {
  name: 'OperatorGroupEdit',
  components: {
    FlyoutMenuHead,
    FormFooter,
    GroupPermissions
  },
  data() {
    return {
      items: {
        mdiCircle: mdiCircle
      },
      operatorGroup: {
        name: '',
        description: '',
        active: true,
        smartGroup: false
      },
      operatorHeaders: [
        { text: 'Username', value: 'username' },
        { text: 'Name', value: 'first_name' },
        { text: 'Gender', value: 'gender' },
        { text: 'Status', value: 'enabled' },
        { text: 'Tags', value: 'tags' },
        { text: 'Locations', value: 'locations' },
        { text: '', value: 'actions', sortable: false }
      ],
      operatorItems: [],
      operatorGroupForm: {
        permissions: {
          selectedClaims: [],
          groupStates: {}
        }
      },
      // Operator management dialog and logic
      availableOperators: [],
      operatorDialog: false,
      tempSelectedOperators: [],
      searchOperators: '',
      updatingOperators: false,
      loading: false,
      error: null
    };
  },
  computed: {
    ...mapGetters('operators', [
      'getGroupDetail',
      'getGroupPermissionsPerGroup',
      'getGroupMembers',
      'getGroupMembersMeta',
      'isLoading',
      'getErrors'
    ]),
    ...mapGetters('accounts', {
      currentUser: 'getUser'
    }),
    selectedItem() {
      return {
        name: 'Operator Group',
      };
    },
    groupUuid() {
      return this.$route.params.uuid;
    },
    filteredAvailableOperators() {
      if (!this.searchOperators) return this.availableOperators;
      const search = this.searchOperators.toLowerCase();
      return this.availableOperators.filter(op =>
        (`${op.first_name} ${op.last_name}`.toLowerCase().includes(search)) ||
        (op.email && op.email.toLowerCase().includes(search)) ||
        (op.username && op.username.toLowerCase().includes(search))
      );
    },
  },
  async mounted() {
    this.loading = true;
    try {
      // Fetch group detail
      await this.fetchOperatorGroup(this.groupUuid);
      // Fetch permissions
      await this.fetchPermissions();
      // Populate form fields
      if (this.getGroupDetail) {
        this.operatorGroup.name = this.getGroupDetail.name || '';
        this.operatorGroup.description = this.getGroupDetail.description || '';
        this.operatorGroup.active = typeof this.getGroupDetail.active === 'boolean' ? this.getGroupDetail.active : true;
        if (typeof this.getGroupDetail.automatic_operator_group === 'boolean') {
          this.operatorGroup.smartGroup = this.getGroupDetail.automatic_operator_group;
        } else if (this.getGroupDetail.type) {
          this.operatorGroup.smartGroup = this.getGroupDetail.type === 'automatic';
        }
      }
      // Fetch available operators for dialog
      if (typeof this.fetchOperators === 'function') {
        this.availableOperators = await this.fetchOperators('page_size=100&page=1');
      }
      // Fetch group members for operatorItems
      await this.fetchOperatorsInGroup({ operator_group_uuid: this.groupUuid, qs: 'page_size=50&page=1' });
      this.operatorItems = this.getGroupMembers || [];
      // Combine permissions for this group
      if (this.getGroupDetail && Array.isArray(this.getGroupDetail.operator_permission_claims)) {
        const groupClaimUuids = this.getGroupDetail.operator_permission_claims.map(claim => claim.uuid);
        await this.$store.dispatch('operators/combinePermissionsForGroup', {
          groupUuid: this.groupUuid,
          groupClaims: groupClaimUuids
        });
      }
    } catch (e) {
      this.error = e;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions('operators', [
      'fetchOperatorGroup',
      'fetchPermissions',
      'fetchOperatorsInGroup',
      'updateOperatorGroup',
      // Add these if available in your store:
      'addOperatorsToGroup',
      'removeOperatorFromGroup',
      'fetchOperators',
    ]),
    ...mapActions('global', ['addAlert']),
    async updateOperator() {
      try {
        this.loading = true;
        // Prepare payload for update
        const payload = {
          name: this.operatorGroup.name,
          description: this.operatorGroup.description,
          active: this.operatorGroup.active,
          // backend currently supports only 'manual' type — keep type manual and
          // use the boolean flag `automatic_operator_group` to mark automatic groups
          type: 'manual',
          automatic_operator_group: this.operatorGroup.smartGroup,
          operator_permission_claim_uuids: this.operatorGroupForm.permissions.selectedClaims || [],
        };
        console.log('Update payload:', JSON.stringify(payload, null, 2));
        await this.updateOperatorGroup({ operator_group_uuid: this.groupUuid, payload });
        this.$emit('save', this.operatorGroup);

        // Show success message and redirect to group view
        this.addAlert({ type: 'success', message: 'Operator group updated successfully' });
        this.$router.push({ name: 'viewOperatorGroup', params: { uuid: this.groupUuid, action: 'view' } });
      } catch (e) {
        console.error('Update error:', e?.response?.data || e);
        this.error = e;

        // Build a helpful message from server response if available
        let serverMsg = '';
        const resp = e && e.response && e.response.data;
        if (!resp) {
          serverMsg = e.message || String(e);
        } else if (typeof resp === 'string') {
          serverMsg = resp;
        } else if (Array.isArray(resp)) {
          serverMsg = resp.join('; ');
        } else if (typeof resp === 'object') {
          const parts = [];
          for (const [k, v] of Object.entries(resp)) {
            if (Array.isArray(v)) {
              parts.push(`${k}: ${v.join(', ')}`);
            } else if (typeof v === 'object') {
              parts.push(`${k}: ${JSON.stringify(v)}`);
            } else {
              parts.push(`${k}: ${v}`);
            }
          }
          serverMsg = parts.join(' | ');
        } else {
          serverMsg = String(resp);
        }

        const message = `Failed to update operator group: ${serverMsg}`;
        this.addAlert({ type: 'error', message });
      } finally {
        this.loading = false;
      }
    },
    onPermissionsFormUpdate(newForm) {
      this.operatorGroupForm = { ...this.operatorGroupForm, ...newForm };
    },
    closeFlyout() {
      this.$router.go(-1);
    },
    // --- Operator management dialog logic ---
    openOperatorDialog() {
      this.tempSelectedOperators = [...this.operatorItems];
      this.operatorDialog = true;
      this.searchOperators = '';
    },
    closeOperatorDialog() {
      this.operatorDialog = false;
      this.tempSelectedOperators = [];
      this.searchOperators = '';
    },
    async confirmOperatorSelection() {
      if (!this.tempSelectedOperators || this.tempSelectedOperators.length === 0) {
        return;
      }
      this.updatingOperators = true;
      try {
        const operatorUuids = this.tempSelectedOperators.map(op => op.uuid);
        const userEmail = this.currentUser?.email || this.currentUser?.user?.email || '';
        await this.addOperatorsToGroup({
          operator_group_uuid: this.groupUuid,
          space_operators: {
            created_by_email: userEmail.trim(),
            operator_uuids: operatorUuids
          }
        });
        // Re-fetch members after adding
        await this.fetchOperatorsInGroup({ operator_group_uuid: this.groupUuid, qs: 'page_size=50&page=1' });
        this.operatorItems = [...(this.getGroupMembers || [])];
        this.closeOperatorDialog();
      } catch (e) {
        console.error('Error adding operators:', e?.response?.data || e);
      } finally {
        this.updatingOperators = false;
      }
    },
    isOperatorSelected(operator) {
      return this.tempSelectedOperators.some(op => op.uuid === operator.uuid);
    },
    toggleOperator(operator) {
      const existingIndex = this.tempSelectedOperators.findIndex(op => op.uuid === operator.uuid);
      if (existingIndex > -1) {
        this.tempSelectedOperators.splice(existingIndex, 1);
      } else {
        this.tempSelectedOperators.push(operator);
      }
    },
    async removeOperatorFromGroup(operator) {
      this.updatingOperators = true;
      await this.removeOperatorFromGroup({
        operator_group_uuid: this.groupUuid,
        operator_uuid: operator.uuid
      });
      await this.fetchOperatorsInGroup({ operator_group_uuid: this.groupUuid, qs: 'page_size=50&page=1' });
      this.operatorItems = this.getGroupMembers || [];
      this.updatingOperators = false;
    },
  },
  watch: {
    getGroupMembers: {
      handler(newMembers) {
        this.operatorItems = [...(newMembers || [])];
      },
      deep: true,
    },
  },
};
</script>


<style scoped>
.operator-group-edit {
  margin: 0 auto;
}

.edit-card {
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.section-header {
  border-bottom: 1px solid var(--v-border-color);
  padding-bottom: 8px;
}

.permission-panels {
  border: 1px solid var(--v-border-color);
  border-radius: 4px;
}

.permission-header {
  background-color: var(--v-surface-base);
  border-bottom: 1px solid var(--v-border-color);
}

.permission-header .v-expansion-panel-header__icon {
  display: none;
}
.positioned-button-top-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.table-style{
    border: 1px solid var(--v-borderColor-base) !important;
}
.scrollable-container {
  max-height: calc(100vh - 115px);
  overflow-y: auto;
  padding-bottom: 50px;
}
.col-border {
  border: 1px solid var(--v-borderColor-base);
  width: 100%;
  height: 40px;
  border-radius: 4px;
}
.row-border {
  border: 1px solid var(--v-borderColor-base);
  border-radius: 4px;
  padding: 10px;
}
</style>